<template>
  <div id="app">
    <div id="my-map"></div>

    <!-- 三维风场控制面板 -->
    <aside class="hud" :class="{ 'hud--collapsed': collapsed }">
      <header class="hud__head" @click="collapsed = !collapsed">
        <div class="hud__title">
          <span class="hud__dot"></span>
          <div>
            <h1>三维风场粒子</h1>
            <p>3D WIND FIELD · GPU PARTICLES</p>
          </div>
        </div>
        <button class="hud__toggle" :aria-label="collapsed ? '展开' : '收起'">
          <span class="chev" :class="{ 'chev--up': !collapsed }"></span>
        </button>
      </header>

      <div class="hud__body" v-show="!collapsed">
        <div class="hud__stats">
          <div class="stat">
            <span class="stat__val">{{ formatCount(particleCount) }}</span>
            <span class="stat__key">粒子数</span>
          </div>
          <div class="stat">
            <span class="stat__val">{{ fps }}</span>
            <span class="stat__key">FPS</span>
          </div>
          <div class="stat">
            <span class="stat__val">{{ running ? 'LIVE' : 'HOLD' }}</span>
            <span class="stat__key">状态</span>
          </div>
        </div>

        <section class="ctrl">
          <div class="ctrl__row">
            <label>风场类型</label>
          </div>
          <div class="seg">
            <button
              class="seg__btn"
              :class="{ 'seg__btn--active': fieldType === 'vortex' }"
              @click="setFieldType('vortex')"
            >台风风场</button>
            <button
              class="seg__btn"
              :class="{ 'seg__btn--active': fieldType === 'regional' }"
              @click="setFieldType('regional')"
            >区域风场</button>
          </div>
        </section>

        <section class="ctrl">
          <div class="ctrl__row">
            <label>弧线模式</label>
            <span class="ctrl__val">{{ arcMode ? '热力图' : '流动粒子' }}</span>
          </div>
          <div class="seg">
            <button
              class="seg__btn"
              :class="{ 'seg__btn--active': !arcMode }"
              @click="setArcMode(false)"
            >流动粒子</button>
            <button
              class="seg__btn"
              :class="{ 'seg__btn--active': arcMode }"
              @click="setArcMode(true)"
            >热力图</button>
          </div>
        </section>

        <section class="ctrl" v-if="arcMode">
          <div class="ctrl__row">
            <label>弧线高度</label>
            <span class="ctrl__val">{{ (arcHeight * 100).toFixed(0) }}%</span>
          </div>
          <input
            class="slider"
            type="range"
            min="0.05"
            max="1.0"
            step="0.01"
            v-model.number="arcHeight"
            @input="applyArcParams"
          />
        </section>

        <section class="ctrl" v-if="!arcMode">
          <div class="ctrl__row">
            <label>粒子密度</label>
            <span class="ctrl__val">{{ formatCount(particleCount) }}</span>
          </div>
          <input
            class="slider"
            type="range"
            min="64"
            max="256"
            step="16"
            :value="texSize"
            @input="onTexSize"
          />
        </section>

        <section class="ctrl" v-if="!arcMode">
          <div class="ctrl__row">
            <label>粒子大小</label>
            <span class="ctrl__val">{{ params.pointSize.toFixed(1) }}px</span>
          </div>
          <input
            class="slider"
            type="range"
            min="1"
            max="30"
            step="0.5"
            v-model.number="params.pointSize"
            @input="applyParams"
          />
        </section>

        <section class="ctrl" v-if="!arcMode">
          <div class="ctrl__row">
            <label>流动速度</label>
            <span class="ctrl__val">{{ (params.speedFactor * 1000).toFixed(0) }}</span>
          </div>
          <input
            class="slider"
            type="range"
            min="0.05"
            max="0.60"
            step="0.01"
            v-model.number="params.speedFactor"
            @input="applyParams"
          />
        </section>

        <section class="ctrl" v-if="!arcMode">
          <div class="ctrl__row">
            <label>粒子寿命</label>
            <span class="ctrl__val">{{ params.lifetime.toFixed(1) }}s</span>
          </div>
          <input
            class="slider"
            type="range"
            min="2"
            max="30"
            step="0.5"
            v-model.number="params.lifetime"
            @input="applyParams"
          />
        </section>

        <section class="ctrl">
          <div class="ctrl__row">
            <label>垂直强度</label>
            <span class="ctrl__val">{{ params.verticalScale.toFixed(1) }}</span>
          </div>
          <input
            class="slider"
            type="range"
            min="0"
            max="4"
            step="0.1"
            v-model.number="params.verticalScale"
            @input="applyParams"
          />
        </section>

        <section class="ctrl">
          <div class="ctrl__row">
            <label>不透明度</label>
            <span class="ctrl__val">{{ Math.round(params.opacity * 100) }}%</span>
          </div>
          <input
            class="slider"
            type="range"
            min="0.1"
            max="1"
            step="0.05"
            v-model.number="params.opacity"
            @input="applyParams"
          />
        </section>

        <section class="ctrl" v-if="!arcMode">
          <div class="ctrl__row">
            <label>拖尾长度</label>
            <span class="ctrl__val">{{ params.trailLength.toFixed(1) }}</span>
          </div>
          <input
            class="slider"
            type="range"
            min="1"
            max="30"
            step="0.5"
            v-model.number="params.trailLength"
            @input="applyParams"
          />
        </section>

        <template v-if="fieldType === 'vortex'">
          <section class="ctrl">
            <div class="ctrl__row">
              <label>涡旋半径</label>
              <span class="ctrl__val">{{ (vortex.radius * 100).toFixed(0) }}%</span>
            </div>
            <input
              class="slider"
              type="range"
              min="0.1"
              max="0.5"
              step="0.01"
              v-model.number="vortex.radius"
              @input="applyVortexParams"
            />
          </section>

          <section class="ctrl">
            <div class="ctrl__row">
              <label>旋转强度</label>
              <span class="ctrl__val">{{ vortex.swirl.toFixed(2) }}</span>
            </div>
            <input
              class="slider"
              type="range"
              min="0.1"
              max="1.0"
              step="0.01"
              v-model.number="vortex.swirl"
              @input="applyVortexParams"
            />
          </section>

          <section class="ctrl">
            <div class="ctrl__row">
              <label>辐合强度</label>
              <span class="ctrl__val">{{ vortex.inflow.toFixed(2) }}</span>
            </div>
            <input
              class="slider"
              type="range"
              min="0.02"
              max="0.4"
              step="0.01"
              v-model.number="vortex.inflow"
              @input="applyVortexParams"
            />
          </section>

          <section class="ctrl">
            <div class="ctrl__row">
              <label>上升气流</label>
              <span class="ctrl__val">{{ vortex.updraft.toFixed(2) }}</span>
            </div>
            <input
              class="slider"
              type="range"
              min="0.1"
              max="0.8"
              step="0.01"
              v-model.number="vortex.updraft"
              @input="applyVortexParams"
            />
          </section>

          <section class="ctrl">
            <div class="ctrl__row">
              <label>外围环境风</label>
              <span class="ctrl__val">{{ (vortex.environment * 100).toFixed(0) }}%</span>
            </div>
            <input
              class="slider"
              type="range"
              min="0"
              max="0.2"
              step="0.005"
              v-model.number="vortex.environment"
              @input="applyVortexParams"
            />
          </section>
        </template>

        <template v-if="fieldType === 'regional'">
          <section class="ctrl">
            <div class="ctrl__row">
              <label>主导风速</label>
              <span class="ctrl__val">{{ regional.baseSpeed.toFixed(2) }}</span>
            </div>
            <input
              class="slider"
              type="range"
              min="0.3"
              max="1.5"
              step="0.01"
              v-model.number="regional.baseSpeed"
              @input="applyRegionalParams"
            />
          </section>

          <section class="ctrl">
            <div class="ctrl__row">
              <label>气流摆动</label>
              <span class="ctrl__val">{{ regional.meander.toFixed(2) }}</span>
            </div>
            <input
              class="slider"
              type="range"
              min="0"
              max="1.5"
              step="0.01"
              v-model.number="regional.meander"
              @input="applyRegionalParams"
            />
          </section>

          <section class="ctrl">
            <div class="ctrl__row">
              <label>垂直对流</label>
              <span class="ctrl__val">{{ regional.updraft.toFixed(2) }}</span>
            </div>
            <input
              class="slider"
              type="range"
              min="0"
              max="0.8"
              step="0.01"
              v-model.number="regional.updraft"
              @input="applyRegionalParams"
            />
          </section>

          <section class="ctrl">
            <div class="ctrl__row">
              <label>扰流强度</label>
              <span class="ctrl__val">{{ regional.turbulence.toFixed(2) }}</span>
            </div>
            <input
              class="slider"
              type="range"
              min="0"
              max="1.5"
              step="0.01"
              v-model.number="regional.turbulence"
              @input="applyRegionalParams"
            />
          </section>
        </template>

        <section class="ctrl">
          <div class="ctrl__row">
            <label>配色主题</label>
          </div>
          <div class="themes">
            <button
              v-for="(t, key) in themes"
              :key="key"
              class="theme"
              :class="{ 'theme--active': activeTheme === key }"
              :style="{ background: `linear-gradient(90deg, ${t.slow}, ${t.fast})` }"
              :title="t.name"
              @click="setTheme(key)"
            ></button>
          </div>
        </section>

        <div class="hud__actions">
          <button class="btn btn--primary" @click="toggleRun">
            {{ running ? '暂停' : '播放' }}
          </button>
          <button class="btn" @click="resetView">复位视角</button>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
/* global Cesium */
import WindParticles from "./utils/cesiumCtrl/wind/WindParticles.js";
import {
  MountainTerrainProvider,
  HillshadeImageryProvider
} from "./utils/cesiumCtrl/mountainTerrain.js";

const CAMERA_HOMES = {
  vortex: { lon: 118.5, lat: 28.0, height: 1500000, pitch: -30, heading: -40 },
  // 区域风场为几公里的小尺度场景：从域南侧远处低角度斜视，
  // 让热力图体积侧面与山头轮廓完整入画
  regional: { lon: 118.5, lat: 27.885, height: 4500, pitch: -12, heading: 0 }
};

// 各风场模式的模拟域。区域风场约 8km x 8km x 2km，只覆盖几个山头的尺度。
const DOMAINS = {
  vortex: {
    minLon: 102.0,
    maxLon: 118.0,
    minLat: 27.0,
    maxLat: 43.0,
    minH: 0,
    maxH: 300000
  },
  regional: {
    minLon: 118.459,
    maxLon: 118.541,
    minLat: 27.964,
    maxLat: 28.036,
    minH: 0,
    maxH: 2000
  }
};

function hexToCartesian3(hex) {
  const n = parseInt(hex.replace("#", ""), 16);
  return new Cesium.Cartesian3(
    ((n >> 16) & 255) / 255,
    ((n >> 8) & 255) / 255,
    (n & 255) / 255
  );
}

export default {
  data() {
    return {
      viewer: null,
      wind: null,
      collapsed: false,
      running: true,
      fps: 0,
      texSize: 320,
      particleCount: 320 * 320,
      activeTheme: "ocean",
      fieldType: "regional",
      arcMode: true,
      arcHeight: 1.0,
      themes: {
        ocean: { name: "海洋", slow: "#3d8bff", fast: "#c2feff" },
        aurora: { name: "极光", slow: "#2fd389", fast: "#eaffb0" },
        ember: { name: "炽焰", slow: "#ff7a45", fast: "#ffe9a8" },
        violet: { name: "星紫", slow: "#9a7bff", fast: "#ffc4ef" }
      },
      params: {
        pointSize: 19,
        speedFactor: 0.16,
        lifetime: 22,
        verticalScale: 0.9,
        opacity: 0.70,
        trailLength: 5.0
      },
      vortex: {
        radius: 0.11,
        swirl: 0.75,
        inflow: 0.16,
        updraft: 0.22,
        environment: 0.10
      },
      regional: {
        baseSpeed: 0.9,
        meander: 0.8,
        updraft: 0.5,
        turbulence: 0.6
      }
    };
  },
  mounted() {
    this.initViewer();
    this.initWind();
    this.wind.arcMode = this.arcMode;
    this.applyParams();
    this.applyVortexParams();
    this.applyRegionalParams();
    this.setTheme(this.activeTheme);
    this.startFpsMeter();
  },
  beforeDestroy() {
    if (this._fpsRemove) this._fpsRemove();
    if (this.wind) this.viewer.scene.primitives.remove(this.wind);
    if (this.viewer) this.viewer.destroy();
  },
  methods: {
    initViewer() {
      // 离线程序化山地底图：山体阴影影像 + 高度图地形，无需 Ion token。
      this.viewer = new Cesium.Viewer("my-map", {
        baseLayerPicker: false,
        homeButton: false,
        sceneModePicker: false,
        navigationHelpButton: false,
        animation: false,
        timeline: false,
        fullscreenButton: false,
        geocoder: false,
        infoBox: false,
        selectionIndicator: false,
        shouldAnimate: true,
        imageryProvider: new HillshadeImageryProvider(),
        terrainProvider: new MountainTerrainProvider()
      });

      this.viewer.scene.globe.enableLighting = false;
      this.viewer.scene.globe.showSkirts = false;
      // 有地形后开启地形深度测试，粒子/体积会被山体正确遮挡
      this.viewer.scene.globe.depthTestAgainstTerrain = true;
      // 粒子着色器写入线性深度，关闭对数深度以保持与地球的深度一致
      // （Cesium 会自动改用多视锥渲染保证精度）。
      this.viewer.scene.logarithmicDepthBuffer = false;

      this.resetView(false);
    },

    initWind() {
      this.wind = new WindParticles({
        viewer: this.viewer,
        particleTexSize: this.texSize,
        fieldType: this.fieldType,
        domain: DOMAINS[this.fieldType],
        arcHeight: this.arcHeight
      });
    },

    applyParams() {
      if (!this.wind) return;
      this.wind.pointSize = this.params.pointSize;
      this.wind.speedFactor = this.params.speedFactor;
      this.wind.lifeDecay = 1 / this.params.lifetime;
      this.wind.verticalScale = this.params.verticalScale;
      this.wind.opacity = this.params.opacity;
      this.wind.trailLength = this.params.trailLength;
    },

    applyVortexParams() {
      if (!this.wind) return;
      this.wind.vortexRadius = this.vortex.radius;
      this.wind.swirlStrength = this.vortex.swirl;
      this.wind.inflowStrength = this.vortex.inflow;
      this.wind.updraftStrength = this.vortex.updraft;
      this.wind.environmentStrength = this.vortex.environment;
    },

    applyRegionalParams() {
      if (!this.wind) return;
      this.wind.fieldType = this.fieldType;
      this.wind.regionalBaseSpeed = this.regional.baseSpeed;
      this.wind.regionalMeander = this.regional.meander;
      this.wind.regionalUpdraft = this.regional.updraft;
      this.wind.regionalTurbulence = this.regional.turbulence;
    },

    setFieldType(type) {
      if (type === this.fieldType) return;
      this.fieldType = type;
      // 两种模式的模拟域尺度差异很大，需要重建粒子系统并切换视角。
      this.rebuildWind();
      this.resetView(true);
    },

    rebuildWind() {
      if (this.wind) {
        this.viewer.scene.primitives.remove(this.wind);
      }
      this.initWind();
      this.applyParams();
      this.applyVortexParams();
      this.applyRegionalParams();
      this.setTheme(this.activeTheme);
      this.wind.arcMode = this.arcMode;
      this.wind.arcHeight = this.arcHeight;
      if (!this.running) this.wind.stop();
    },

    setArcMode(enabled) {
      this.arcMode = enabled;
      if (this.wind) this.wind.arcMode = enabled;
    },

    applyArcParams() {
      if (this.wind) this.wind.arcHeight = this.arcHeight;
    },

    onTexSize(e) {
      const n = Number(e.target.value);
      this.texSize = n;
      this.particleCount = n * n;
      if (this.wind) this.wind.setParticleCount(n * n);
    },

    setTheme(key) {
      this.activeTheme = key;
      const t = this.themes[key];
      if (this.wind) {
        this.wind.colorSlow = hexToCartesian3(t.slow);
        this.wind.colorFast = hexToCartesian3(t.fast);
      }
    },

    toggleRun() {
      this.running = !this.running;
      if (this.wind) {
        this.running ? this.wind.start() : this.wind.stop();
      }
    },

    resetView(animate = true) {
      const home = CAMERA_HOMES[this.fieldType] || CAMERA_HOMES.vortex;
      const dest = Cesium.Cartesian3.fromDegrees(
        home.lon,
        home.lat,
        home.height
      );
      const orientation = {
        heading: Cesium.Math.toRadians(home.heading || 0),
        pitch: Cesium.Math.toRadians(home.pitch),
        roll: 0
      };
      if (animate) {
        this.viewer.camera.flyTo({ destination: dest, orientation, duration: 1.6 });
      } else {
        this.viewer.camera.setView({ destination: dest, orientation });
      }
    },

    startFpsMeter() {
      let frames = 0;
      let last = performance.now();
      const remove = this.viewer.scene.postRender.addEventListener(() => {
        frames++;
        const now = performance.now();
        if (now - last >= 1000) {
          this.fps = Math.round((frames * 1000) / (now - last));
          frames = 0;
          last = now;
        }
      });
      this._fpsRemove = remove;
    },

    formatCount(n) {
      return n >= 1000 ? (n / 1000).toFixed(n % 1000 === 0 ? 0 : 1) + "k" : String(n);
    }
  }
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body {
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #05070d;
}

#app {
  width: 100vw;
  height: 100vh;
  position: relative;
  font-family: "Rajdhani", "Segoe UI", system-ui, sans-serif;
}

#my-map {
  height: 100%;
  width: 100%;
}

.cesium-viewer-bottom,
.cesium-widget-credits {
  display: none !important;
}

/* ---------------- HUD panel ---------------- */
.hud {
  position: absolute;
  top: 18px;
  left: 18px;
  z-index: 999;
  width: 288px;
  color: #dbe7f5;
  background: linear-gradient(
    160deg,
    rgba(13, 22, 40, 0.9),
    rgba(8, 13, 26, 0.86)
  );
  border: 1px solid rgba(94, 180, 255, 0.18);
  border-radius: 14px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(14px);
  overflow: hidden;
  transition: width 0.3s ease;
}

.hud::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, #54d6ff, transparent);
  opacity: 0.8;
}

.hud__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  user-select: none;
}

.hud__title {
  display: flex;
  align-items: center;
  gap: 11px;
}

.hud__dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #54d6ff;
  box-shadow: 0 0 10px #54d6ff, 0 0 20px rgba(84, 214, 255, 0.5);
  animation: pulse 2.4s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.55;
    transform: scale(0.82);
  }
}

.hud__title h1 {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 1px;
  line-height: 1.1;
  color: #f2f7ff;
}

.hud__title p {
  font-family: "Share Tech Mono", monospace;
  font-size: 9.5px;
  letter-spacing: 1.5px;
  color: #6f8bb3;
  margin-top: 2px;
}

.hud__toggle {
  background: rgba(84, 214, 255, 0.08);
  border: 1px solid rgba(84, 214, 255, 0.2);
  width: 26px;
  height: 26px;
  border-radius: 7px;
  display: grid;
  place-items: center;
  cursor: pointer;
  transition: background 0.2s;
}

.hud__toggle:hover {
  background: rgba(84, 214, 255, 0.18);
}

.chev {
  width: 8px;
  height: 8px;
  border-right: 2px solid #8fd4ff;
  border-bottom: 2px solid #8fd4ff;
  transform: rotate(45deg);
  margin-top: -3px;
  transition: transform 0.3s;
}

.chev--up {
  transform: rotate(-135deg);
  margin-top: 3px;
}

.hud__body {
  padding: 4px 16px 16px;
}

.hud__stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin: 6px 0 16px;
}

.stat {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 9px;
  padding: 8px 6px;
  text-align: center;
}

.stat__val {
  display: block;
  font-family: "Share Tech Mono", monospace;
  font-size: 16px;
  color: #7fe6ff;
  line-height: 1;
}

.stat__key {
  display: block;
  font-size: 10px;
  letter-spacing: 1px;
  color: #6f8bb3;
  margin-top: 5px;
}

.ctrl {
  margin-bottom: 15px;
}

.ctrl__row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 7px;
}

.ctrl__row label {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: #b9cbe4;
}

.ctrl__val {
  font-family: "Share Tech Mono", monospace;
  font-size: 12px;
  color: #7fe6ff;
}

/* ---------------- slider ---------------- */
.slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 4px;
  background: rgba(120, 160, 220, 0.18);
  outline: none;
  cursor: pointer;
}

.slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #eaf6ff;
  border: 3px solid #38b6ff;
  box-shadow: 0 0 8px rgba(56, 182, 255, 0.7);
  transition: transform 0.15s, box-shadow 0.15s;
}

.slider::-webkit-slider-thumb:hover {
  transform: scale(1.18);
  box-shadow: 0 0 14px rgba(56, 182, 255, 0.95);
}

.slider::-moz-range-thumb {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background: #eaf6ff;
  border: 3px solid #38b6ff;
  box-shadow: 0 0 8px rgba(56, 182, 255, 0.7);
  cursor: pointer;
}

/* ---------------- field type segment ---------------- */
.seg {
  display: flex;
  gap: 8px;
}

.seg__btn {
  flex: 1;
  padding: 8px 0;
  font-family: "Rajdhani", sans-serif;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #8aa4c6;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(120, 170, 240, 0.18);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.seg__btn:hover {
  color: #d9e9ff;
  background: rgba(120, 170, 240, 0.12);
}

.seg__btn--active {
  color: #04121f;
  background: linear-gradient(120deg, #54d6ff, #38b6ff);
  border-color: transparent;
  box-shadow: 0 4px 14px rgba(56, 182, 255, 0.4);
}

/* ---------------- themes ---------------- */
.themes {
  display: flex;
  gap: 9px;
}

.theme {
  flex: 1;
  height: 26px;
  border-radius: 7px;
  border: 2px solid transparent;
  cursor: pointer;
  transition: transform 0.15s, border-color 0.2s, box-shadow 0.2s;
}

.theme:hover {
  transform: translateY(-2px);
}

.theme--active {
  border-color: #eaf6ff;
  box-shadow: 0 0 12px rgba(127, 230, 255, 0.5);
}

/* ---------------- actions ---------------- */
.hud__actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

.btn {
  flex: 1;
  padding: 9px 0;
  font-family: "Rajdhani", sans-serif;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #bcd4f2;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(120, 170, 240, 0.22);
  border-radius: 9px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn:hover {
  background: rgba(120, 170, 240, 0.12);
  color: #ffffff;
}

.btn--primary {
  color: #04121f;
  background: linear-gradient(120deg, #54d6ff, #38b6ff);
  border-color: transparent;
  box-shadow: 0 6px 18px rgba(56, 182, 255, 0.35);
}

.btn--primary:hover {
  color: #04121f;
  filter: brightness(1.08);
  box-shadow: 0 8px 22px rgba(56, 182, 255, 0.5);
}
</style>
